/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, NgForm} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatCalendar} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {FabMessageService} from '@fabric/components/message';
import {endOfToday, isWeekend} from 'date-fns';
import {Employee} from '../../admin/models/employee.model';
import {PublicHoliday} from '../../admin/models/publicHoliday.model';
import {Vacation} from '../../admin/models/vacation.model';
import {WorkAbroad} from '../../admin/models/workAbroad.model';
import {AuthenticationService} from '../../core/authentication/authentication.service';
import {PersonalService} from '../../personal/personal.service';
import {countries} from '../countryDataStore';
import {WorkAbroadService} from '../work-abroad.service';

@Component({
    selector: 'app-work-abroad-pop-up',
    templateUrl: './work-abroad-pop-up.component.html',
    styleUrls: ['./work-abroad-pop-up.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class WorkAbroadPopUpComponent implements OnInit {
    public numDaysAbroad = 0;
    public vacationDays = 0;
    public employee = {} as Employee;
    public request = {} as WorkAbroad;
    public isEditing = {} as WorkAbroad;
    public model = {} as WorkAbroad;
    public countries = countries;
    public countryNameArray: string[] = [];
    public countryNameArray2: string[] = [];
    public negative2 = -2;
    public uniquePublicHoliday: any[] = [];
    public isPageLoading = false;
    public modifyRequest: boolean = false;
    public isDuplicated = false;
    public terms: boolean = false;
    public recording: boolean = false;
    public globalStart = '';
    public globalEnd = '';
    public expandDialog = false;
    public daysSelected: any[] = [];
    public filteredHoliday: any[] = [];
    public event: any;
    public minWorkAbroadDate = new Date();
    public maxWorkAbroadDate = new Date();
    public minWorkAbroadVacationDate = new Date();
    public maxWorkAbroadVacationDate = new Date();
    public minWorkAbroadEditVacationDate = new Date();
    public maxWorkAbroadEditVacationDate = new Date();
    public tempStartDate: string = '';
    public tempEndDate: string = '';
    public publicHolidays: PublicHoliday[] = [];
    public publicHolidayDates: Date[] = [];
    public formattedStartDate: string = '';
    public formattedEndDate: string = '';
    public startEndRange = new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
    });
    public notForNextYearWork = false;
    public isNextTrip = false;
    public nextTripAllowed = '';
    public preApprovedVacations: any[] = [];

    public isSelected = (event: any) => {
        const date = event.getFullYear() + '-' +
            ('00' + (event.getMonth() + 1)).slice(this.negative2) + '-' +
            ('00' + event.getDate()).slice(this.negative2);
        return this.daysSelected.find(x => x == date) ? 'selected' : '';
    };

    public makeWeekendUnselectable = (d: any): boolean => {
        const day = d.getDay();
        this.filteredHoliday = [];
        return day !== 0 && day !== 6;
    }

    public makePublicHolidayUnselectable = (d: any): boolean => {
        const day = d.getDay();
        const startDate = String(new Date(this.startEndRange.value.start!));
        const endDate = String(new Date(this.startEndRange.value.end!));
        this.getUniquePublicHolidays();
        for (const start = new Date(this.startEndRange.value.start!); start <= new Date(this.startEndRange.value.end!); start.setDate(start.getDate() + 1)) {
            this.uniquePublicHoliday.forEach((date) => {
                if (date >= this.personalService.convertDateFormatForAPI(startDate) && date <= this.personalService.convertDateFormatForAPI(endDate)) {
                    this.filteredHoliday.push(date);
                }
            });
        }
        this.filteredHoliday = this.personalService.getUniqueValues(this.filteredHoliday).sort();
        return day !== 0 && day !== 6 && !this.filteredHoliday.find(x => x == this.workAbroadService.convertDateString(d));
    }

    @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
    @ViewChild('form') private readonly form!: NgForm;
    @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;

    constructor(private readonly workAbroadService: WorkAbroadService,
                private readonly personalService: PersonalService,
                private readonly fabMessageService: FabMessageService,
                private readonly formBuilder: FormBuilder,
                private authService: AuthenticationService,
                public dialogRef: MatDialogRef<[]>,
                @Inject(MAT_DIALOG_DATA) public selectedData: any) {
    }

    ngOnInit(): void {
        this.employee = this.selectedData.employee;
        this.request = this.selectedData.workAbroad;
        this.numDaysAbroad = this.selectedData.numDaysAbroad;
        this.vacationDays = this.selectedData.vacationDays;
        this.isEditing = this.selectedData.isEditing;
        if (this.request === null) {
            this.model.updatedBy = this.authService.userInfo.gmin || '';
            this.model.employeeId = this.employee.id;
            this.model.requestStatus = 'PENDING';
            this.model.vacations = [];
        } else {
            this.model = this.selectedData.workAbroad;
            this.tempStartDate = this.request.startDate;
            this.tempEndDate = this.request.endDate;
            this.formattedStartDate = this.formatDate(this.request.startDate);
            this.formattedEndDate = this.formatDate(this.request.endDate);
            this.setGlobalDates();
            this.startEndRange.patchValue({
                start: new Date(this.request.startDate),
                end: new Date(this.request.endDate)
            });
            this.modifyRequest = true;
        }
        if (this.request === null) {
            this.model.numberOfWorkAbroadDays = 0;
        } else if (this.model.numberOfWorkAbroadDays != 0) {
            this.model.numberOfWorkAbroadDays = this.numDaysAbroad;
        }
        this.regionValidation();
        this.countryNameArray = this.workAbroadService.getCountryNameArray(this.countries);
        this.countryNameArray2 = this.countryNameArray;
        this.setMinWorkAbroadDate();
        this.setMaxWorkAbroadDate();
        if (this.selectedData.editVacationDates == 'edit') {
            this.setMinEditVacationDate();
            this.setMaxEditVacationDate();
        } else {
            this.setMinVacationDate();
            this.setMaxVacationDate();
        }
        this.getPublicHolidays();
    }

    /**
     * Takes a string, number or Date object as input and
     * returns a formatted date string in the format 'DD/MM/YYYY'.
     * Pads if necessary with leading 0s
     * @param dateString
     */
    formatDate(dateString: string | number | Date) {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /**
     * Closes the popup
     */
    close(): void {
        this.dialogRef.close();
    }

    /**
     * Loop through countries list, check for employees region and remove it from the list
     */
    public regionValidation() {
        for (let i = 0; i < this.countries.length; i++) {
            if (this.selectedData.employee.region === this.countries[i].code3) {
                this.countries.splice(i, 1);
                i--
            }
        }
    }

    /**
     * When mat calendar is opened set the active date to start value of work abroad dates
     */
    onMatMenuOpen() {
        if (this.request === null && this.startEndRange.value.start !== null) {
            this.calendar.activeDate = <Date>this.startEndRange.value.start;
        } else if (this.request !== null) {
            this.setMinEditVacationDate();
            this.setMaxEditVacationDate();
            this.calendar.activeDate = this.minWorkAbroadEditVacationDate;
        } else {
            this.calendar.activeDate = this.minWorkAbroadVacationDate;
        }
    }

    /**
     * Fetches a list of public holidays
     */
    public getPublicHolidays(): void {
        this.personalService.fetchPublicHolidayList(this.employee.region).subscribe({
            next: (data) => {
                this.publicHolidays = data;
                this.getPublicHolidayDates();
            }, error: (err: HttpErrorResponse) => {
                this.fabMessageService.displayErrorMessage(err.error.errorMessage);
            }
        });
    }

    public getUniquePublicHolidays(): void {
        let value: any[] = [];
        this.publicHolidayDates.forEach((i) => {
            value.push(this.personalService.convertDateFormatForAPI(String(i)));
        })
        this.uniquePublicHoliday = this.personalService.getUniqueValues(value);
    }

    /**
     * Gets an array of public holiday dates from the public holiday array
     */
    public getPublicHolidayDates(): void {
        this.publicHolidays.forEach((holiday) => {
            this.publicHolidayDates.push(new Date(this.personalService.swapDateAndMonth(holiday.holidayDate)));
        });
    }

    /**
     * Calls a service method to set the minimum work abroad date
     */
    public setMinWorkAbroadDate(): void {
        if (this.startEndRange.value.start !== null) {
            this.minWorkAbroadDate = this.workAbroadService.getCurrentWorkAbroadAllowance(this.employee.employeeWorkAbroadAllowance, this.minWorkAbroadDate);
        } else {
            this.minWorkAbroadDate = this.workAbroadService.getCurrentWorkAbroadAllowance(this.employee.employeeWorkAbroadAllowance,
                new Date('01-02-2023'));
        }
    }

    /**
     * Calls a service method to set the maximum work abroad date
     */
    public setMaxWorkAbroadDate(): void {
        if (this.selectedData.employee.employeeWorkAbroadAllowance.length === 0 || this.isNextTrip) {
            this.maxWorkAbroadDate = this.workAbroadService.convertDateToNextYearEnd(new Date());
        } else {
            this.selectedData.employee.employeeWorkAbroadAllowance.forEach((i: any) => {
                let date = new Date();
                if (new Date(i.rollingPeriodEnd) > date) {
                    i.nextTripAllowedFrom = this.personalService.convertDateFormatForAPI(i.nextTripAllowedFrom);
                    if (i.nextTripAllowedFrom === this.selectedData.nextTrip) {
                        this.maxWorkAbroadDate = i.rollingPeriodEnd;
                    }
                    if (this.selectedData.isEditing) {
                        this.maxWorkAbroadDate = i.rollingPeriodEnd;
                    }
                } else if (this.selectedData.editVacationDates !== 'edit') {
                    i.nextTripAllowedFrom = this.personalService.convertDateFormatForAPI(i.nextTripAllowedFrom);
                    this.selectedData.rollingPeriod = this.formatDateReverse(this.selectedData.rollingPeriod);
                    this.maxWorkAbroadDate = this.selectedData.rollingPeriod;
                } else {
                    this.maxWorkAbroadDate = this.workAbroadService.convertDateToNextYearEnd(new Date());
                }
            });
        }
    }

    forNextYear() {
        if (!this.notForNextYearWork) {
            this.isNextTrip = true;
            this.setMaxWorkAbroadDate();
        } else {
            this.isNextTrip = false;
            this.setMaxWorkAbroadDate();
        }
    }

    /**
     * Calls a service method to set the minimum vacation date
     */
    public setMinVacationDate(): void {
        let currentDate = new Date('');
        if (this.minWorkAbroadVacationDate <= currentDate) {
            this.minWorkAbroadVacationDate = currentDate;
        } else {
            let tempDate = this.personalService.convertDateFormatForAPI(String(this.minWorkAbroadVacationDate));
            this.minWorkAbroadVacationDate = new Date(this.workAbroadService.convertDateToVacationFomat(tempDate));
        }
    }

    /**
     * Calls the method to set the minimum edit vacation date for the work abroad request.
     */
    public setMinEditVacationDate(): void {
        const start = this.startEndRange.value.start;
        const end = this.startEndRange.value.end;
        if (start && end) {
            if (start !== this.startEndRange.value.start || end !== this.startEndRange.value.start) {
                this.maxWorkAbroadEditVacationDate = end;
                this.minWorkAbroadEditVacationDate = start;
            }
        } else {
            this.minWorkAbroadEditVacationDate = new Date(this.request.startDate);
        }
    }

    /**
     * Calls a service method to set the maximum vacation date
     */
    public setMaxVacationDate(): void {
        let tempDate = this.personalService.convertDateFormatForAPI(String(this.maxWorkAbroadVacationDate));
        this.maxWorkAbroadVacationDate = new Date(this.workAbroadService.convertDateToVacationFomat(tempDate));
    }

    /**
     * Calls the method to set the maximum edit vacation date for the work abroad request.
     */
    public setMaxEditVacationDate(): void {
        const start = this.startEndRange.value.start;
        const end = this.startEndRange.value.end;
        if (start && end) {
            if (start !== this.startEndRange.value.start || end !== this.startEndRange.value.end) {
                this.maxWorkAbroadEditVacationDate = end;
                this.minWorkAbroadEditVacationDate = start;
            }
        } else {
            this.maxWorkAbroadEditVacationDate = new Date(this.request.endDate);
        }
    }

    /**
     * Sets the available vacation range based on the selected work abroad days
     * @param start the new minWorkAbroadVacationDate value
     * @param end the new maxWorkAbroadVacationDate value
     */
    public changeAvailableVacationRange(start: string, end: string): void {
        let counter: any[] = [];
        this.preApprovedVacations = [];
        this.setMaxWorkAbroadDate();
        this.minWorkAbroadVacationDate = new Date(this.workAbroadService.convertDateToVacationFomat(start));
        this.maxWorkAbroadVacationDate = new Date(this.workAbroadService.convertDateToVacationFomat(end));
        this.daysSelected.forEach((i) => {
            let value = new Date(this.workAbroadService.convertVacationDateFormatToWorkAbroadFormat(i));
            if (value >= this.startEndRange.value.start! && value <= this.startEndRange.value.end!) {
                counter.push(i);
            }
        });
        this.daysSelected = counter;
        this.employee.vacation.forEach((vacation: Vacation) => {
            const [startDay, startMonth, startYear] = vacation.startDate.split('/');
            let vacationStartDate: Date = new Date(+startYear, +startMonth - 1, +startDay);

            const [endDay, endMonth, endYear] = vacation.endDate.split('/');
            let vacationEndDate: Date = new Date(+endYear, +endMonth - 1, +endDay);

            if (vacationStartDate >= this.minWorkAbroadVacationDate && vacationStartDate <= this.maxWorkAbroadVacationDate && vacation.requestStatus === 'APPROVED') {
                //Front is partially
                this.markApprovedVacations(vacation, this.calendar);
            } else if (vacationEndDate >= this.minWorkAbroadVacationDate && vacationEndDate <= this.maxWorkAbroadVacationDate && vacation.requestStatus === 'APPROVED') {
                // end is partially
                this.markApprovedVacations(vacation, this.calendar);
            }
        });
        this.calculateWorkAbroadDays();
    }

    /**
     * Removes public holiday from work abroad range and vacation date selected
     */
    public removePublicHolidaySelected(): void {
        let count = 0;
        this.daysSelected.forEach((i) => {
            count++
            this.uniquePublicHoliday.forEach((holiday) => {
                let result = this.workAbroadService.checkPublicHoliday(i, holiday);
                if (result == 0) {
                    this.daysSelected.splice(this.daysSelected.indexOf(count), 1);
                }
            });
        });
        this.calculateWorkAbroadDays();
    }

    /**
     * Calculates the number of work abroad days from the form control values
     */
    public calculateWorkAbroadDays(): void {
        this.getUniquePublicHolidays();
        this.startEndRange.value.start!.setHours(6);
        this.startEndRange.value.end!.setHours(6);
        this.model.numberOfWorkAbroadDays = Math.floor(((Date.UTC(this.startEndRange.value.end!.getFullYear(), this.startEndRange.value.end!.getMonth(), this.startEndRange.value.end!.getDate()) - Date.UTC(this.startEndRange.value.start!.getFullYear(), this.startEndRange.value.start!.getMonth(), this.startEndRange.value.start!.getDate())) / (1000 * 60 * 60 * 24)) + 1);
        for (let day = new Date(this.startEndRange.value.start!); day <= new Date(this.startEndRange.value.end!); day.setDate(day.getDate() + 1)) {
            if (isWeekend(day)) {
                this.model.numberOfWorkAbroadDays--;
            }
            this.validatePublicHoliday(day);
        }
        if (this.daysSelected.length > 0) {
            this.model.numberOfWorkAbroadDays -= this.daysSelected.length;
        }
        if (this.preApprovedVacations.length) {
            this.model.numberOfWorkAbroadDays -= this.preApprovedVacations.length;
        }
        if (this.daysSelected.length != this.numDaysAbroad && this.selectedData.editVacationDates == 'edit') {
            this.numDaysAbroad = this.model.numberOfWorkAbroadDays;
        }
        this.checkShutdownDaysInRange();
    }

    public checkShutdownDaysInRange() {

        if (this.preApprovedVacations.length) {

            const startDate = new Date(this.startEndRange.value.start!);
            const endDate = new Date(this.startEndRange.value.end!);

            this.publicHolidays.forEach((holiday) => {
                const holidayDate = new Date(this.personalService.swapDateAndMonth(holiday.holidayDate));
                if (holiday.shutdownDay && holidayDate >= startDate && holidayDate <= endDate) {
                    console.log(true);
                    this.model.numberOfWorkAbroadDays++
                }
            });
        }
    }

    public validatePublicHoliday(day: Date) {
        this.uniquePublicHoliday.forEach((date) => {
            if (this.personalService.convertDateFormatForAPI(String(day)) == date) {
                this.model.numberOfWorkAbroadDays--;
            }
        });
    }

    /**
     * Adds or removes values in the daysSelected array
     * @param event the date to be added
     * @param calendar the calendar to update
     */
    public select(event: any, calendar: any): void {

        const date = event.getFullYear() + '-' +
            ('00' + (event.getMonth() + 1)).slice(this.negative2) + '-' +
            ('00' + event.getDate()).slice(this.negative2);
        const index = this.daysSelected.findIndex(x => x == date);

        if (this.preApprovedVacations.findIndex(x => x == date) > -1) {
            return;
        }

        if (index < 0) {
            this.daysSelected.push(date);
        } else {
            this.daysSelected.splice(index, 1);
        }
        if (this.selectedData.editVacationDates == 'edit') {
            this.vacationDays = this.daysSelected.length + this.preApprovedVacations.length;
        }
        calendar.updateTodaysDate();
    }

    public markApprovedVacations(vacation: Vacation, calendar: any): void {
        const [startDay, startMonth, startYear] = vacation.startDate.split('/');
        let vacationStartDate: Date = new Date(+startYear, +startMonth - 1, +startDay);
        const [endDay, endMonth, endYear] = vacation.endDate.split('/');
        let vacationEndDate: Date = new Date(+endYear, +endMonth - 1, +endDay);

        // we got 3 types, partially inside from the end, completely or from the front. Best suggestion is to iterate and check where day lands
        for (let d = vacationStartDate; d <= vacationEndDate; d.setDate(d.getDate() + 1)) {
            if (vacationStartDate >= this.minWorkAbroadVacationDate && vacationStartDate <= this.maxWorkAbroadVacationDate && vacationEndDate >= this.minWorkAbroadVacationDate && vacationEndDate <= this.maxWorkAbroadVacationDate) {
                const date = d.getFullYear() + '-' +
                    ('00' + (d.getMonth() + 1)).slice(this.negative2) + '-' +
                    ('00' + d.getDate()).slice(this.negative2);
                this.preApprovedVacations.push(date);
            }
        }

        this.vacationDays = this.daysSelected.length + this.preApprovedVacations.length;
    }

    /**
     * Assigns the checkbox value to the correct boolean
     * @param box the checkbox name
     * @param ob the value to change to
     */
    public checkboxChange(box: string, ob: MatCheckboxChange): void {
        if (box === 'terms') {
            this.terms = ob.checked;
        } else if (box === 'recording') {
            this.recording = ob.checked;
        }
    }

    /**
     * Sets values for globalStart and globalEnd date after swapping the month and date
     */
    public setGlobalDates(): void {
        this.globalStart = this.personalService.swapDateAndMonth(this.model.startDate);
        this.globalEnd = this.personalService.swapDateAndMonth(this.model.endDate);
    }

    /**
     * Sets the start and end date for the model from the formGroup then formats them to be accepted by the api.
     * Also assigns values to the lastDayWorking and firstDayBack fields.
     */
    public setDateFormat(): void {
        this.model.startDate = this.startEndRange.value.start!.toString();
        this.model.startDate = this.personalService.convertDateFormatForAPI(this.model.startDate);
        this.model.endDate = this.startEndRange.value.end!.toString();
        this.model.endDate = this.personalService.convertDateFormatForAPI(this.model.endDate);
        this.model.lastDayWorking = this.workAbroadService.getLastDayWorking(this.startEndRange.value.start!, this.publicHolidays, this.employee.vacation);
        this.model.firstDayBack = this.workAbroadService.getFirstDayBack(this.startEndRange.value.end!, this.publicHolidays, this.employee.vacation);
    }

    /**
     * Sets vacation data and adds the vacation objects to the work abroad model
     */
    public setVacationData(): void {
        if (this.daysSelected.length > 0) {
            this.model.vacations = [];
            let tempVacation: Vacation = {} as Vacation;
            this.daysSelected.sort((a, b) => (new Date(a).getTime() - new Date(b).getTime()));
            this.daysSelected.forEach((data) => {
                const vacation = {} as Vacation;
                vacation.employeeId = this.model.employeeId;
                vacation.startDate = this.personalService.convertDateFormatForAPI(data);
                vacation.endDate = this.personalService.convertDateFormatForAPI(data);
                vacation.halfDay = false;
                vacation.halfDayType = 'FULL';
                vacation.absenceReason = 'Work Abroad Request for ' + this.model.country;
                vacation.region = this.employee.region;
                vacation.requestStatus = 'PENDING';
                vacation.absenceType = 'HOLIDAY';
                vacation.updatedBy = this.model.updatedBy;
                if (tempVacation.startDate === undefined) {
                    tempVacation = vacation;
                } else if (this.checkNextDate(tempVacation.endDate, vacation.startDate)) {
                    tempVacation.endDate = vacation.endDate;
                } else {
                    this.model.vacations.push(tempVacation);
                    tempVacation = vacation;
                }
            });
            this.model.vacations.push(tempVacation);
        } else {
            if (!this.isEditing) {
                this.model.vacations = [];
            }
        }
    }

    /**
     * Duplication check if a work abroad request already exists for dates.
     * Checks all dates between the start and end date of an already existing request
     */
    public workAbroadAlreadyExists() {
        for (const element of this.employee.workAbroad) {
            if (element.requestStatus !== 'CANCELLED' && element.requestStatus !== 'DECLINED') {
                const dateFrom = this.formatDate(element.startDate);
                const dateTo = this.formatDate(element.endDate);
                const dateCheckStart = this.model.startDate;
                const dateCheckEnd = this.model.endDate;

                const d1 = dateFrom.split('/');
                const d2 = dateTo.split('/');
                const c1 = dateCheckStart.split('/');
                const c2 = dateCheckEnd.split('/');

                const from = new Date(Number(d1[2]), parseInt(d1[1]) - 1, Number(d1[0]));
                const to = new Date(Number(d2[2]), parseInt(d2[1]) - 1, Number(d2[0]));
                const checkStart = new Date(Number(c1[2]), parseInt(c1[1]) - 1, Number(c1[0]));
                const checkEnd = new Date(Number(c2[2]), parseInt(c2[1]) - 1, Number(c2[0]));
                if (checkStart >= from && checkStart <= to) {
                    this.isDuplicated = checkStart >= from && checkStart <= to;
                    return false;
                } else if (checkEnd >= from && checkEnd <= to) {
                    this.isDuplicated = true;
                    return false;
                } else if (checkEnd.getTime() == from.getTime()) {
                    this.isDuplicated = true;
                    return false;
                }
            }
        }
    }

    /**
     * Increases a date by one and checks if it is equal to the second date, returning true if so
     * @param dateOne The first date string to compare
     * @param dateTwo The second date string to compare
     */
    public checkNextDate(dateOne: string, dateTwo: string): boolean {
        const tempDate1 = new Date(this.personalService.swapDateAndMonth(dateOne));
        tempDate1.setDate(tempDate1.getDate() + 1);
        const tempDateApi1 = this.personalService.convertDateFormatForAPI(tempDate1.toDateString());
        const tempDate2 = new Date(this.personalService.swapDateAndMonth(dateTwo));
        const tempDateApi2 = this.personalService.convertDateFormatForAPI(tempDate2.toDateString());

        return tempDateApi1 === tempDateApi2;
    }

    /**
     * Checks that the user has enough workAbroadDays to make a request
     * Ensures that the user can't book a trip for more than 20 days at a time
     */
    public checkWorkAbroadAllowance() {
        if (!this.startEndRange.value || !this.startEndRange.value.start || !this.startEndRange.value.end) {
            return false;
        }

        let workAllowance = this.validateStartDate();

        return workAllowance > 0
            && workAllowance >= this.model.numberOfWorkAbroadDays
            && (this.model.numberOfWorkAbroadDays - this.preApprovedVacations.length) <= 20;
    }

    /**
     * Checks that the user has enough holidays to make a request
     */
    public checkHolidayAllowance() {
        if (this.daysSelected.length == 0) {
            return true;
        }

        let pendingAllowance = 0;
        let selectedVacationDays = this.daysSelected.sort();

        selectedVacationDays.forEach((vacationDay) => {
            this.employee.employeeVacationAllowance.forEach((vacationAllowance) => {
                if (selectedVacationDays.length > 0 && vacationDay.split("-")[0] == vacationAllowance.year) {
                    pendingAllowance = vacationAllowance.vacationAllowanceRemaining;
                }
            });
        })

        return pendingAllowance > 0 && pendingAllowance >= this.daysSelected.length;
    }

    /**
     * Submits a request to the api to create or modify a work abroad request
     */
    public submitForm(): void {
        this.calculateWorkAbroadDays();
        this.setDateFormat();
        this.setStartAndEndDate();
        this.setVacationData();
        this.workAbroadAlreadyExists();
        this.isPageLoading = true;
        if (this.modifyRequest && !this.isDuplicated) {
            this.workAbroadService.modifyWorkAbroadRequest(this.model).subscribe({
                next: () => {
                    this.fabMessageService.displayMessage('Request submitted successfully');
                    this.model = this.request;
                    this.isPageLoading = false;
                    this.close();
                }, error: (err: HttpErrorResponse) => {
                    this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                    this.isPageLoading = false;
                }
            });
        } else if (!this.isDuplicated) {
            this.workAbroadService.submitWorkAbroadRequest(this.model).subscribe({
                next: () => {
                    this.fabMessageService.displayMessage('Request submitted successfully');
                    this.isPageLoading = false;
                    this.close();
                }, error: (err: HttpErrorResponse) => {
                    this.fabMessageService.displayErrorMessage(err.error.errorMessage);
                    this.isPageLoading = false;
                }
            });
        }
        if (this.isDuplicated && this.selectedData.editVacationDates !== 'edit') {
            this.fabMessageService.displayErrorMessage('Booking already made for this date!');
            this.isPageLoading = false;
        }
    }

    /**
     * Set Start and End date for the work abroad request
     */
    public setStartAndEndDate() {
        let startDate = this.model.startDate;
        let endDate = this.model.endDate;

        startDate = this.personalService.swapDateAndMonth(startDate);
        endDate = this.personalService.swapDateAndMonth(endDate);

        this.startEndRange.value.start!.setDate(new Date(startDate).getDate());
        this.startEndRange.value.start!.setMonth(new Date(startDate).getMonth());
        this.startEndRange.value.start!.setFullYear(new Date(startDate).getUTCFullYear());

        this.startEndRange.value.end!.setDate(new Date(endDate).getDate());
        this.startEndRange.value.end!.setMonth(new Date(endDate).getMonth());
        this.startEndRange.value.end!.setFullYear(new Date(endDate).getUTCFullYear());
    }

    /**
     * Changes a variable to say if the user has expanded the terms and conditions or not
     */
    hasClickedTermsAndConditions() {
        this.expandDialog = !this.expandDialog;
    }

    /**
     * Returns a string depending on whether the dialog has been expanded or not
     * @param open
     */
    expandDialogIcon(open: boolean) {
        if (open) {
            return 'fa-caret-up';
        } else {
            return 'fa-caret-down';
        }
    }

    /**
     * Validates work abroad request if there is work allowance for the requested year
     */
    public validateStartDate() {
        let isOverlap = false;
        let workAllowance = 0;
        let requestedStartDate = new Date(this.startEndRange.value.start!);
        let requestedEndDate = new Date(this.startEndRange.value.end!);

        requestedStartDate.setHours(0);
        requestedEndDate.setHours(0);
        if (this.employee.employeeWorkAbroadAllowance.length === 0 || this.isNextTrip) {
            workAllowance = 40;
        }

        this.employee.employeeWorkAbroadAllowance.forEach((i) => {
            let rollingStart = new Date(i.rollingPeriodStart);
            let rollingEnd = new Date(i.rollingPeriodEnd);
            let nextTripAllowed;
            nextTripAllowed = this.formatDateReverse(i.nextTripAllowedFrom);
            nextTripAllowed = new Date(nextTripAllowed);
            nextTripAllowed.setHours(0);

            if (requestedStartDate > rollingEnd) {
                return;
            }
            if (requestedEndDate > rollingEnd) {
                isOverlap = true;
            }

            if (requestedStartDate >= rollingStart && requestedEndDate <= rollingEnd && requestedStartDate >= nextTripAllowed) {
                workAllowance = i.allowanceRemaining;
            } else if (isOverlap) {
                workAllowance = 40;
                // The user wants to book backdated requests:
            } else if (requestedEndDate < endOfToday() && requestedEndDate <= rollingEnd) {
                workAllowance = i.allowanceRemaining;
            } else {
                workAllowance = 0;
            }
        });

        return workAllowance
    }

    /**
     * Takes a string, number or Date object as input and
     * returns a formatted date string in the format 'DD/MM/YYYY'.
     * Pads if necessary with leading 0s
     * @param dateString
     */
    formatDateReverse(dateString: any) {
        const date = dateString;
        const day = date.split('/')[0];
        const month = date.split('/')[1];
        const year = date.split('/')[2];
        return `${year}-${month}-${day}`;
    }
}
